import React, { FC, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { CalendarOutlined, EnvironmentOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Card, Space, Typography } from 'antd';
import ImageC from '../Image';
import cn from "src/utils/cn";

const { Title, Text, Paragraph } = Typography;

interface EventCardProps {
	imageUrl: string;
	title: string;
	description?: string;
	date: string;
	location: string;
	participants: string;
	slug: string;
	showButton?: boolean;
	size?: 'small' | 'large';
	onClick?: () => void;
	buttonText?: string;
}

interface DetailItemProps {
	icon: ReactNode;
	text: string;
	isSmall?: boolean;
}

const DetailItem: FC<DetailItemProps> = ({ icon, text, isSmall = false }) => (
	<Space align="center" size={isSmall ? 4 : 8}>
		<span
			className={cn('d-flex align-items-center', {
				'fs-12': isSmall,
				'fs-14': !isSmall,
			})}
		>
			{icon}
		</span>
		<Text type="secondary" className={isSmall ? 'fs-12' : 'fs-14'}>
			{text}
		</Text>
	</Space>
);

const EventCard: FC<EventCardProps> = ({
	imageUrl,
	title,
	description,
	date,
	location,
	participants,
	slug,
	showButton = false,
	size = 'large',
	onClick,
	buttonText = 'Registrasi Sekarang',
}) => {
	const router = useRouter();
	const isSmall = size === 'small';

	const handleButtonClick = () => {
		if (onClick) {
			onClick();
		} else {
			router.push(`/event/${slug}`);
		}
	};

	return (
		<Card
			style={{ borderRadius: '12px', overflow: 'hidden', width: '100%' }}
			bodyStyle={{ padding: 0 }}
		>
			<div
				className={cn(
					'flex',
					isSmall ? 'p-3 flex-row' : 'p-3 md:p-4 flex-col lg:flex-row',
				)}
			>
				<div className="flex items-center justify-center">
					<div
						className={cn(
							'relative rounded-md overflow-hidden',
							isSmall
								? 'h-[90px] w-[90px] mr-3 shrink-0 sm:h-[120px] sm:w-[120px] sm:mr-4'
								: 'h-[200px] w-[200px] mb-5 mr-0 sm:h-[260px] sm:w-[260px] lg:mr-5 lg:mb-0',
						)}
					>
						<ImageC
							alt={title}
							src={imageUrl}
							fill
              				className="w-full h-full object-contain"
							defaultSrc='/icons/512x512.png' 
						/>
					</div>
				</div>

				<div className="flex flex-col justify-between">
					<div>
						<Title
							level={isSmall ? 5 : 4}
							style={{ marginTop: 0, marginBottom: isSmall ? '4px' : '12px' }}
							className={isSmall ? 'fs-14 fs-md-16 fw-600 mb-1' : 'fs-18 fs-md-22'}
						>
							{title}
						</Title>

						{!isSmall && description && (
							<Paragraph
								type="secondary"
								ellipsis={{ rows: 3 }}
								className={isSmall ? 'fs-12 mb-2' : 'fs-14'}
							>
								{description}
							</Paragraph>
						)}
					</div>

					<Space
						direction="vertical"
						size={isSmall ? 2 : 5}
						style={{ marginBottom: isSmall ? '12px' : '24px' }}
					>
						<DetailItem
							icon={<CalendarOutlined className="text-primary" />}
							text={date}
							isSmall={isSmall}
						/>
						<DetailItem
							icon={<EnvironmentOutlined className="text-primary" />}
							text={location}
							isSmall={isSmall}
						/>
						{!isSmall && (
							<DetailItem
								icon={<UserOutlined className="text-primary" />}
								text={participants}
								isSmall={isSmall}
							/>
						)}
					</Space>

					<div
						className={cn(
							'flex items-center',
							isSmall ? 'flex-row mt-2' : 'flex-col sm:flex-row',
						)}
					>
						{showButton && (
							<Button
								type="primary"
								size={isSmall ? 'middle' : 'large'}
								style={{ borderRadius: '8px' }}
								className={cn(
									isSmall
										? 'fs-13 fw-600 w-auto self-start'
										: 'fs-15 fw-600 w-full sm:w-auto sm:self-end',
								)}
								onClick={handleButtonClick}
							>
								{buttonText}
							</Button>
						)}
					</div>
				</div>
			</div>
		</Card>
	);
};

export default EventCard;
